import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckCircle2, Sparkles, ArrowRight, Copy, ExternalLink } from "lucide-react";

/**
 * `/team-invite/:code` — public onboarding page.
 * A worker taps the shared master link, tells us who they are + what to
 * call their site, and gets a fully branded tenant + login instantly.
 * No account required, no payment.
 */
export default function TeamInvite() {
  const { code } = useParams();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [checking, setChecking] = useState(true);
  const [f, setF] = useState({
    business_name: "",
    admin_email: "",
    admin_name: "",
    preferred_subdomain: "",
    primary_color: "#2E4FB8",
    contact_phone: "",
    logo_url: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/team-invite/${code}`)
      .then(r => setInvite(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Invite link is not active."))
      .finally(() => setChecking(false));
  }, [code]);

  const slugPreview = (f.preferred_subdomain || "yourname").toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "").slice(0, 32) || "yourname";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post(`/team-invite/${code}/redeem`, f);
      setDone(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Redeem failed. Ask your team admin for a fresh link.");
    } finally { setSubmitting(false); }
  };

  if (checking) return <div className="min-h-screen grid place-items-center text-muted-foreground">Checking invite…</div>;
  if (!invite) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <Card className="p-8 max-w-md text-center">
          <div className="label-caps text-destructive mb-2">Invite not active</div>
          <p className="text-sm text-muted-foreground mb-4">This team invite link isn't active anymore. Ask your team admin for a fresh one.</p>
          <Button asChild variant="outline"><Link to="/">Back home</Link></Button>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-background paper-texture grid place-items-center p-6">
        <Card className="p-8 max-w-lg w-full" data-testid="team-invite-success">
          <div className="text-center mb-6">
            <CheckCircle2 size={48} className="mx-auto text-primary mb-3" />
            <div className="label-caps text-[10px] text-primary mb-2">Your site is ready</div>
            <h1 className="font-serif text-3xl mb-2">{f.business_name}</h1>
            <p className="text-sm text-muted-foreground">Save these — they're the keys to your new site.</p>
          </div>
          <div className="space-y-3 mb-6">
            <div className="rounded-md border border-border p-3">
              <div className="label-caps text-[10px] mb-1">Your public site</div>
              <div className="flex items-center gap-2">
                <a href={done.branded_url} target="_blank" rel="noreferrer" className="font-mono text-sm text-primary underline break-all flex-1" data-testid="team-invite-branded-url">{done.branded_url}</a>
                <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(done.branded_url); toast.success("Site URL copied"); }}>
                  <Copy size={12} />
                </Button>
              </div>
            </div>
            <div className="rounded-md border border-primary/40 bg-primary/5 p-3">
              <div className="label-caps text-[10px] mb-1 text-primary">Login credentials</div>
              <div className="grid grid-cols-1 gap-1 text-sm">
                <div><span className="text-muted-foreground">Email:</span> <span className="font-mono">{done.admin_email}</span></div>
                <div>
                  <span className="text-muted-foreground">Temp password:</span>{" "}
                  <span className="font-mono select-all bg-secondary px-1.5 py-0.5 rounded" data-testid="team-invite-temp-pw">{done.admin_temp_password}</span>
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">Change it after your first login.</div>
              </div>
            </div>
          </div>
          <Button asChild className="w-full" size="lg" data-testid="team-invite-goto-login">
            <a href={done.login_url}>Go to my site <ArrowRight size={14} className="ml-1" /></a>
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background paper-texture p-6">
      <div className="max-w-2xl mx-auto py-12 space-y-8" data-testid="team-invite-page">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 text-primary mb-2">
            <Sparkles size={14} />
            <span className="label-caps text-[10px]">Invited by {invite.invited_by?.brand_name || "your team"}</span>
          </div>
          <h1 className="font-serif text-4xl lg:text-5xl leading-tight mb-3">Set up your own site.</h1>
          <p className="text-muted-foreground max-w-md mx-auto">
            You get a fully branded tax practice site — your logo, your color, your subdomain — plus full LMS access. No charge, courtesy of your team.
          </p>
        </div>

        <form onSubmit={submit}>
          <Card className="p-6 space-y-5">
            <div>
              <Label className="label-caps text-[10px]">Your business name</Label>
              <Input
                required
                placeholder="Alice's Tax Prep"
                value={f.business_name}
                onChange={e => setF({ ...f, business_name: e.target.value })}
                data-testid="team-invite-business-name"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="label-caps text-[10px]">Your name</Label>
                <Input required value={f.admin_name} onChange={e => setF({ ...f, admin_name: e.target.value })} data-testid="team-invite-admin-name" />
              </div>
              <div>
                <Label className="label-caps text-[10px]">Your work email</Label>
                <Input required type="email" value={f.admin_email} onChange={e => setF({ ...f, admin_email: e.target.value })} data-testid="team-invite-email" />
              </div>
            </div>

            <div>
              <Label className="label-caps text-[10px]">Preferred subdomain</Label>
              <div className="flex items-center gap-2">
                <Input
                  required
                  minLength={2}
                  maxLength={32}
                  placeholder="alice"
                  value={f.preferred_subdomain}
                  onChange={e => setF({ ...f, preferred_subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                  className="font-mono"
                  data-testid="team-invite-subdomain"
                />
                <span className="text-xs text-muted-foreground whitespace-nowrap font-mono">.dynamictax.online</span>
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">Your site will live at <code className="text-foreground">{slugPreview}.dynamictax.online</code></div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="label-caps text-[10px]">Brand color</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={f.primary_color} onChange={e => setF({ ...f, primary_color: e.target.value })} className="h-10 w-14 rounded border border-input cursor-pointer" data-testid="team-invite-color" />
                  <Input value={f.primary_color} onChange={e => setF({ ...f, primary_color: e.target.value })} className="font-mono text-xs" />
                </div>
              </div>
              <div>
                <Label className="label-caps text-[10px]">Phone (optional)</Label>
                <Input value={f.contact_phone} onChange={e => setF({ ...f, contact_phone: e.target.value })} placeholder="555-123-4567" />
              </div>
            </div>

            <div>
              <Label className="label-caps text-[10px]">Logo URL (optional — you can upload one later)</Label>
              <Input value={f.logo_url} onChange={e => setF({ ...f, logo_url: e.target.value })} placeholder="https://…/logo.png" className="font-mono text-xs" />
            </div>

            <div className="border-t border-border pt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <CheckCircle2 size={12} className="text-primary" />
                Free · your login is created instantly
              </div>
              <Button type="submit" size="lg" disabled={submitting} data-testid="team-invite-submit">
                {submitting ? "Setting up…" : <>Create my site <ArrowRight size={14} className="ml-1" /></>}
              </Button>
            </div>
          </Card>
        </form>
      </div>
    </div>
  );
}
